import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, fmtEUR } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles, Instagram } from "lucide-react";
import { useLists } from "@/hooks/useLists";

const DEFAULT_PARTNER_TYPES = ["hotel", "b&b", "campeggio", "stabilimento", "tour operator", "agenzia", "altro"];
const DEFAULT_PARTNER_STATUS = ["proposta", "attivo", "sospeso"];
const DEFAULT_CAMP_STATUS = ["idea", "programmata", "pubblicata", "conclusa"];
const DEFAULT_CAMP_KIND = ["post", "reel", "story", "evento", "email", "volantino"];

export default function Marketing() {
  const { lists } = useLists();
  const PARTNER_TYPES = lists.partnership_types?.length ? lists.partnership_types : DEFAULT_PARTNER_TYPES;
  const PARTNER_STATUS = lists.partnership_statuses?.length ? lists.partnership_statuses : DEFAULT_PARTNER_STATUS;
  const CAMP_STATUS = lists.campaign_statuses?.length ? lists.campaign_statuses : DEFAULT_CAMP_STATUS;
  const CAMP_KIND = lists.campaign_kinds?.length ? lists.campaign_kinds : DEFAULT_CAMP_KIND;
  const [campaigns, setCampaigns] = useState([]);
  const [partners, setPartners] = useState([]);
  const [openC, setOpenC] = useState(false);
  const [openP, setOpenP] = useState(false);
  const [ai, setAi] = useState(null);
  const [loadingAi, setLoadingAi] = useState(false);
  const [formC, setFormC] = useState({ title: "", platform: "instagram", kind: "post", date: new Date().toISOString().slice(0, 10), cost: 0, goal: "", status: "programmata" });
  const [formP, setFormP] = useState({ name: "", type: "hotel", contact: "", phone: "", status: "proposta", notes: "" });

  const load = async () => {
    const [c, p] = await Promise.all([api.get("/marketing/campaigns"), api.get("/marketing/partnerships")]);
    setCampaigns(c.data); setPartners(p.data);
  };
  useEffect(() => { load(); }, []);

  const submitC = async (e) => { e.preventDefault(); await api.post("/marketing/campaigns", { ...formC, cost: Number(formC.cost) }); toast.success("Campagna creata"); setOpenC(false); load(); };
  const submitP = async (e) => { e.preventDefault(); await api.post("/marketing/partnerships", formP); toast.success("Partnership aggiunta"); setOpenP(false); load(); };
  const delC = async (id) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/marketing/campaigns/${id}`); load(); };
  const delP = async (id) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/marketing/partnerships/${id}`); load(); };

  const genAi = async () => {
    setLoadingAi(true); setAi(null);
    try { const { data } = await api.post("/marketing/ai-suggestions", {}); setAi(data); }
    catch { toast.error("Errore AI"); }
    setLoadingAi(false);
  };

  const totalCost = campaigns.reduce((s, c) => s + Number(c.cost || 0), 0);

  return (
    <div data-testid="marketing-page">
      <PageHeader eyebrow="Marketing" title="Sardegna & clienti"
        subtitle="Campagne Instagram, partnership locali e strategie stagionali per il territorio di Orosei."
        actions={<Button data-testid="ai-marketing-btn" onClick={genAi} disabled={loadingAi} className="bg-neutral-900 hover:bg-neutral-800 text-[#FDFBF7]"><Sparkles size={14} strokeWidth={1.5} /> {loadingAi ? "Generazione…" : "Suggerimenti AI"}</Button>} />

      {ai && (
        <div className="border border-[#E2D9C8] rounded-lg p-6 bg-[#F8F4EB] mb-8" data-testid="ai-suggestions">
          <div className="flex items-center gap-2 mb-3"><Sparkles size={14} strokeWidth={1.5} /><div className="label-eyebrow">Idee marketing · {ai.month} 2026</div></div>
          <div className="text-sm whitespace-pre-wrap leading-relaxed">{ai.suggestions}</div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Campagne */}
        <div>
          <div className="flex justify-between items-center mb-3">
            <h2 className="font-serif text-2xl">Campagne & post</h2>
            <Dialog open={openC} onOpenChange={setOpenC}>
              <DialogTrigger asChild><Button data-testid="new-campaign-btn" variant="outline" size="sm"><Plus size={14} strokeWidth={1.5} /> Nuova</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-serif">Nuova campagna</DialogTitle></DialogHeader>
                <form onSubmit={submitC} className="space-y-3">
                  <div><Label>Titolo</Label><Input data-testid="camp-title" required value={formC.title} onChange={(e) => setFormC({ ...formC, title: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Piattaforma</Label><Input value={formC.platform} onChange={(e) => setFormC({ ...formC, platform: e.target.value })} /></div>
                    <div><Label>Tipo</Label><Select value={formC.kind} onValueChange={(v) => setFormC({ ...formC, kind: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{CAMP_KIND.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent></Select></div>
                    <div><Label>Data</Label><Input type="date" value={formC.date} onChange={(e) => setFormC({ ...formC, date: e.target.value })} /></div>
                    <div><Label>Costo (€)</Label><Input type="number" step="0.01" value={formC.cost} onChange={(e) => setFormC({ ...formC, cost: e.target.value })} /></div>
                  </div>
                  <div><Label>Obiettivo</Label><Input value={formC.goal} onChange={(e) => setFormC({ ...formC, goal: e.target.value })} /></div>
                  <DialogFooter><Button data-testid="camp-submit" type="submit" className="bg-neutral-900 text-[#FDFBF7]">Salva</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <div className="text-xs text-neutral-500 mb-2">Costo totale programmato: {fmtEUR(totalCost)}</div>
          <div className="space-y-2">
            {campaigns.map((c) => (
              <div key={c.id} className="border border-[#E2D9C8] rounded-lg p-4 bg-white" data-testid={`camp-${c.id}`}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-xs">
                      <Instagram size={12} strokeWidth={1.5} />
                      <span className="label-eyebrow">{c.platform} · {c.kind}</span>
                      <span className={`px-2 py-0.5 rounded-full ${c.status === "pubblicata" ? "bg-emerald-100 text-emerald-800" : c.status === "programmata" ? "bg-sky-100 text-sky-800" : "bg-neutral-100 text-neutral-600"}`}>{c.status}</span>
                    </div>
                    <div className="font-serif text-lg mt-1">{c.title}</div>
                    <div className="text-xs text-neutral-500 mt-1">{c.date} · {fmtEUR(c.cost)}</div>
                    {c.goal && <div className="text-sm text-neutral-600 mt-2">{c.goal}</div>}
                  </div>
                  <button onClick={() => delC(c.id)} className="text-neutral-400 hover:text-red-700"><Trash2 size={14} strokeWidth={1.5} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Partnerships */}
        <div>
          <div className="flex justify-between items-center mb-3">
            <h2 className="font-serif text-2xl">Partnership locali</h2>
            <Dialog open={openP} onOpenChange={setOpenP}>
              <DialogTrigger asChild><Button data-testid="new-partner-btn" variant="outline" size="sm"><Plus size={14} strokeWidth={1.5} /> Nuova</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-serif">Nuova partnership</DialogTitle></DialogHeader>
                <form onSubmit={submitP} className="space-y-3">
                  <div><Label>Nome</Label><Input data-testid="part-name" required value={formP.name} onChange={(e) => setFormP({ ...formP, name: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Tipo</Label><Select value={formP.type} onValueChange={(v) => setFormP({ ...formP, type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{PARTNER_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
                    <div><Label>Stato</Label><Select value={formP.status} onValueChange={(v) => setFormP({ ...formP, status: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{PARTNER_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
                    <div><Label>Referente</Label><Input value={formP.contact} onChange={(e) => setFormP({ ...formP, contact: e.target.value })} /></div>
                    <div><Label>Telefono</Label><Input value={formP.phone} onChange={(e) => setFormP({ ...formP, phone: e.target.value })} /></div>
                  </div>
                  <div><Label>Note / Accordo</Label><Input value={formP.notes} onChange={(e) => setFormP({ ...formP, notes: e.target.value })} /></div>
                  <DialogFooter><Button data-testid="part-submit" type="submit" className="bg-neutral-900 text-[#FDFBF7]">Salva</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <div className="space-y-2">
            {partners.map((p) => (
              <div key={p.id} className="border border-[#E2D9C8] rounded-lg p-4 bg-white" data-testid={`part-${p.id}`}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="label-eyebrow capitalize">{p.type}</span>
                      <span className={`px-2 py-0.5 rounded-full ${p.status === "attivo" ? "bg-emerald-100 text-emerald-800" : p.status === "proposta" ? "bg-amber-100 text-amber-800" : "bg-neutral-100 text-neutral-600"}`}>{p.status}</span>
                    </div>
                    <div className="font-serif text-lg mt-1">{p.name}</div>
                    {p.contact && <div className="text-xs text-neutral-500 mt-1">{p.contact} · {p.phone}</div>}
                    {p.notes && <div className="text-sm text-neutral-600 mt-2">{p.notes}</div>}
                  </div>
                  <button onClick={() => delP(p.id)} className="text-neutral-400 hover:text-red-700"><Trash2 size={14} strokeWidth={1.5} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
